import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom'; 
import { db, auth } from '../firebaseConfig'; 
import { collection, onSnapshot, doc, writeBatch, serverTimestamp, deleteDoc, updateDoc } from "firebase/firestore";

import { 
    Users, UploadCloud, Database, History, BarChart3, IndianRupee, 
    AlertTriangle, Rocket, RefreshCw, ImagePlus, Wand2, Send, 
    Loader2, X, CheckCircle, Trash2, CheckSquare, MessageSquare, 
    Bold, Italic, Strikethrough, Zap, Edit2, FileText
} from 'lucide-react';

const ClientAudience = ({ clientId, onRecharge }) => {
  
  const currentClientId = clientId || auth?.currentUser?.uid || ""; 

  const [clientData, setClientData] = useState(null); 
  const [leads, setLeads] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isAutoSettingUp, setIsAutoSettingUp] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedForCampaign, setSelectedForCampaign] = useState([]);
  const [isCampaignRunning, setIsCampaignRunning] = useState(false);
  const [progressCount, setProgressCount] = useState(0);

  const [selectedRows, setSelectedRows] = useState([]);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [customMessage, setCustomMessage] = useState("");

  const [activeTab, setActiveTab] = useState('DATABASE'); 
  const [campaignHistory, setCampaignHistory] = useState([]);

  const [metaTemplates, setMetaTemplates] = useState([]);
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState("");
  const [templateVariables, setTemplateVariables] = useState([]); 
  const [campaignImage, setCampaignImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const MESSAGE_COST = 0.80; 
  const LOW_BALANCE_THRESHOLD = 50.00;

  const templates = [
      { title: "Special Offer 🎉", text: "Hi [Name]! 🎉 Hamare premium courses par aaj *20% OFF* mil raha hai. Reply *'YES'* to claim!" },
      { title: "New Batch 🚀", text: "Hello [Name]! Naya batch _Monday_ se start ho raha hai. Apna seat confirm karne ke liye abhi reply karein." },
      { title: "Feedback ⭐", text: "Hi [Name]! Aapka hamare sath experience kaisa raha? Kripya apna feedback yahan share karein: [Link]" }
  ];

  useEffect(() => {
    if (!currentClientId) return;
    const unsub = onSnapshot(doc(db, "clients", currentClientId), (docSnap) => {
      if (docSnap.exists()) setClientData(docSnap.data());
    });
    return () => unsub();
  }, [currentClientId]);

  useEffect(() => {
    if (!currentClientId) return;
    const leadsRef = collection(db, "clients", currentClientId, "leads");
    const unsub = onSnapshot(leadsRef, (snapshot) => {
      const leadsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      leadsData.sort((a, b) => {
          const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp?.getTime ? a.timestamp.getTime() : 0);
          const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp?.getTime ? b.timestamp.getTime() : 0);
          return timeB - timeA;
      });
      setLeads(leadsData);
    });
    return () => unsub();
  }, [currentClientId]);

  useEffect(() => {
      setCampaignHistory([
          { id: 1, date: new Date(), name: "Welcome Blast", sent: 10, failed: 0, total: 10, cost: 10 * MESSAGE_COST, type: "CUSTOM" }
      ]);
  }, [currentClientId]);

  useEffect(() => {
      if (currentClientId) {
          handleFetchTemplates();
      }
  }, [currentClientId]);

  const handleSelectAll = (e) => {
      if (e.target.checked) setSelectedRows(leads.map(l => l.id)); 
      else setSelectedRows([]);
  };

  const handleSelectRow = (id) => {
      setSelectedRows(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };

  const handleBulkDelete = async () => {
      if (!window.confirm(`Kya aap sach mein in ${selectedRows.length} leads ko delete karna chahte hain?`)) return;
      const batch = writeBatch(db);
      selectedRows.forEach(id => batch.delete(doc(db, "clients", currentClientId, "leads", id)));
      try { await batch.commit(); setSelectedRows([]); } catch (error) { alert("Delete error."); }
  };

  const handleBulkStatusChange = async (newStatus) => {
      if (!newStatus) return;
      const batch = writeBatch(db);
      selectedRows.forEach(id => batch.update(doc(db, "clients", currentClientId, "leads", id), { status: newStatus }));
      try { await batch.commit(); setSelectedRows([]); } catch (error) { alert("Status update error."); }
  };

  const deleteSingleLead = async (id) => {
      if (!window.confirm("Is lead ko delete karein?")) return;
      try { await deleteDoc(doc(db, "clients", currentClientId, "leads", id)); } catch (error) {}
  };

  const handleEditName = async (id, currentName, e) => {
      e.stopPropagation(); 
      const newName = window.prompt("Lead ka naya naam enter karein:", currentName !== "Student" ? currentName : "");
      if (newName && newName.trim() !== "") {
          try {
              await updateDoc(doc(db, "clients", currentClientId, "leads", id), {
                  studentName: newName.trim(),
                  whatsappName: newName.trim(),
                  name: newName.trim(),
                  updatedAt: serverTimestamp()
              });
          } catch (error) {
              alert("❌ Naam update error: " + error.message);
          }
      }
  };

  const canAffordCampaign = (count) => {
      const totalCost = count * MESSAGE_COST;
      return (clientData?.walletBalance || 0) >= totalCost;
  };

  const triggerRechargeModal = () => {
      if (onRecharge) {
          onRecharge(); 
      } else {
          alert("Payment gateway is not connected. Please use the Settings tab.");
      }
  };

  const openCampaignPreview = () => {
    const manuallySelectedEnrolled = leads.filter(l => selectedRows.includes(l.id) && l.status === "ENROLLED");
    const targetLeads = manuallySelectedEnrolled.length > 0 ? manuallySelectedEnrolled.slice(0, 5) : leads.filter(l => l.status === "ENROLLED").slice(0, 5);
    if (targetLeads.length === 0) return alert("Koi 'ENROLLED' student nahi mila.");
    setSelectedForCampaign(targetLeads); 
    setIsPreviewOpen(true);
  };

  // 🛡️ API SECURED: triggerDashboardCampaign
  const handleLaunchCampaign = async () => {
    if (!canAffordCampaign(selectedForCampaign.length)) return alert(`❌ Recharge Required! Balance low hai.`);

    setIsCampaignRunning(true); setProgressCount(0);
    const interval = setInterval(() => { setProgressCount(prev => prev < selectedForCampaign.length ? prev + 1 : prev); }, 800);

    try {
        const userToken = await auth.currentUser.getIdToken(); // 👈 TOKEN FETCH
        const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/triggerDashboardCampaign", {
            method: "POST", 
            headers: { 
                "Content-Type": "application/json",
                "Authorization": `Bearer ${userToken}` // 👈 TOKEN BHEJA
            },
            body: JSON.stringify({ clientId: currentClientId, audienceType: "ENROLLED" })
        });
        const data = await response.json();
        clearInterval(interval);
        
        if (response.ok && data.success) {
            setProgressCount(selectedForCampaign.length);
            setCampaignHistory(prev => [{ id: Date.now(), date: new Date(), name: "Auto SEO Drip", sent: data.count, failed: 0, total: data.count, cost: data.count * MESSAGE_COST, type: "SEO_DRIP" }, ...prev]);
            setTimeout(() => {
                alert(`✅ Successful! ${data.count} review links sent.`);
                setIsPreviewOpen(false); setIsCampaignRunning(false); setSelectedRows([]); 
            }, 600);
        } else { alert(`❌ Error: ${data.error}`); setIsCampaignRunning(false); }
    } catch (error) { clearInterval(interval); alert("❌ Backend Error."); setIsCampaignRunning(false); }
  };

  const handleFetchTemplates = async () => {
      if (!currentClientId) return;
      setIsFetchingTemplates(true);
      try {
          const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/getClientTemplates", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ clientId: currentClientId })
          });
          const data = await response.json();
          if (response.ok && data.success) {
              setMetaTemplates(data.templates);
          }
      } catch (error) { console.error("API Connect Error."); }
      setIsFetchingTemplates(false);
  };

  const handleAutoSetupTemplates = async () => {
      if (!window.confirm("Install Universal Master Template to your Meta account?")) return;
      setIsAutoSettingUp(true);
      try {
          const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/autoSetupMetaTemplates", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ clientId: currentClientId })
          });
          const data = await response.json();
          
          if (response.ok && data.success) {
              alert("✅ Success! Master Template submitted. Meta will approve it in 30-60 seconds. Please click 'Sync Meta' after a moment.");
              setTimeout(() => handleFetchTemplates(), 2000); 
          } else { alert(`❌ Meta Error: ${data.error}`); }
      } catch (error) { alert("❌ Server Connection Error."); }
      setIsAutoSettingUp(false);
  };

  const handleTemplateSelect = (e) => {
      const idx = e.target.value;
      setSelectedTemplateIndex(idx);
      setCustomMessage(""); 

      if (idx !== "" && metaTemplates[idx]) {
          const bodyComponent = metaTemplates[idx].components.find(c => c.type === 'BODY');
          if (bodyComponent && bodyComponent.text) {
              const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
              const count = matches ? matches.length : 0;
              setTemplateVariables(new Array(count).fill("")); 
          } else {
              setTemplateVariables([]);
          }
      } else {
          setTemplateVariables([]);
      }
  };

  const handleVariableChange = (index, value) => {
      const newVars = [...templateVariables];
      newVars[index] = value;
      setTemplateVariables(newVars);
  };

  // 🛡️ API SECURED: sendBulkCustomMessage
  const handleSendCustomMessage = async () => {
      if (!customMessage.trim() && !campaignImage) return alert("Pehle message likhein ya image attach karein!");
      if (selectedRows.length === 0) return alert("Kam se kam ek lead select karein.");
      if (!canAffordCampaign(selectedRows.length)) return alert(`❌ Wallet Balance Low! Recharge karein.`);
      if (!window.confirm(`Kya aap in ${selectedRows.length} logo ko WhatsApp message bhejna chahte hain?`)) return;

      setIsCampaignRunning(true); setProgressCount(0);
      const interval = setInterval(() => { setProgressCount(prev => prev < selectedRows.length ? prev + 1 : prev); }, 500);

      try {
          const userToken = await auth.currentUser.getIdToken(); // 👈 TOKEN FETCH
          const response = await fetch("https://us-central1-whatsapp-autoreply-bot-pivot.cloudfunctions.net/sendBulkCustomMessage", {
              method: "POST", 
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${userToken}` // 👈 TOKEN BHEJA
              },
            body: JSON.stringify({ 
                  clientId: currentClientId, 
                  leadIds: selectedRows, 
                  messageText: customMessage,
                  templateVariables: templateVariables, 
                  templateName: selectedTemplateIndex !== "" ? metaTemplates[selectedTemplateIndex].name : undefined,
                  languageCode: selectedTemplateIndex !== "" ? metaTemplates[selectedTemplateIndex].language : undefined
              })
          });
          const data = await response.json();
          clearInterval(interval);
          if (response.ok && data.success) {
              setProgressCount(selectedRows.length);
              setCampaignHistory(prev => [{ id: Date.now(), date: new Date(), name: "Custom Broadcast", sent: data.count, failed: selectedRows.length - data.count, total: selectedRows.length, cost: data.count * MESSAGE_COST, type: "CUSTOM" }, ...prev]);
              setTimeout(() => {
                  alert(`✅ Broadcast Successful! ${data.count} messages sent.`);
                  setIsComposeOpen(false); setIsCampaignRunning(false); setCustomMessage(""); removeImage(); setSelectedRows([]);
              }, 600);
          } else { alert(`❌ Error: ${data.error}`); setIsCampaignRunning(false); }
      } catch (error) { clearInterval(interval); alert("❌ API Error."); setIsCampaignRunning(false); }
  };

  const downloadSampleCSV = () => {
    const headers = "Phone,Name,Status\n";
    const rows = "919876543210,Rahul Sharma,ENROLLED\n918888888888,Priya Singh,DROPPED";
    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", "Pivot_CRM_Template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      if (!currentClientId) return alert("System Error: Client ID missing.");
      if (!file.name.endsWith('.csv')) { alert("❌ Kripya sirf .CSV file upload karein."); event.target.value = null; return; }
  
      setIsUploading(true); setUploadStatus('Scanning CSV...');
  
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result.replace(/\r/g, ''); 
        const rows = text.split('\n').filter(row => row.trim().length > 0);
        if (rows.length < 2) { alert("❌ CSV empty hai!"); setIsUploading(false); return; }
  
        const headers = rows[0].split(',').map(h => h.replace(/[^a-zA-Z0-9]/g, '').toLowerCase());
        const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('number') || h.includes('mobile'));
        const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('student'));
        const statusIdx = headers.findIndex(h => h.includes('status'));
  
        if (phoneIdx === -1) { alert("❌ CSV mein 'Phone' column nahi mila!"); setIsUploading(false); return; }
  
        const validRows = rows.slice(1); setUploadStatus(`Saving records...`);
  
        try {
          const batch = writeBatch(db);
          let count = 0;
          validRows.forEach(row => {
            const cols = row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(c => c.replace(/^"|"$/g, '').trim());
            const rawPhone = cols[phoneIdx];
            const cleanPhone = rawPhone?.replace(/\D/g, "");
            if (!cleanPhone || cleanPhone.length < 10) return;
  
            batch.set(doc(db, "clients", currentClientId, "leads", cleanPhone), {
              phone: cleanPhone,
              studentName: (nameIdx !== -1 ? cols[nameIdx] : "Student") || "Student",
              status: (statusIdx !== -1 && cols[statusIdx] ? cols[statusIdx].toUpperCase() : "ENROLLED"),
              source: "CSV Upload", timestamp: serverTimestamp() 
            }, { merge: true });
            count++;
          });
          await batch.commit(); alert(`✅ Success! ${count} naye records add ho gaye.`);
        } catch (error) { alert("❌ Upload failed."); }
        setIsUploading(false); event.target.value = null; setUploadStatus('');
      };
      reader.readAsText(file);
  };

  const handleImageSelect = (e) => {
      const file = e.target.files[0];
      if (file) {
          if (file.size > 5 * 1024 * 1024) return alert("Image 5MB se choti honi chahiye (WhatsApp limit).");
          setCampaignImage(file);
          setImagePreviewUrl(URL.createObjectURL(file));
      }
  };

  const removeImage = () => {
      setCampaignImage(null);
      setImagePreviewUrl(null);
  };

  const insertText = (format) => setCustomMessage(prev => prev + format);
  
  const formatWhatsAppText = (text) => {
      if (!text) return "Type a message to see preview...";
      let formatted = text.replace(/\[Name\]/gi, "Rahul").replace(/\*(.*?)\*/g, "<strong>$1</strong>").replace(/_(.*?)_/g, "<em>$1</em>").replace(/~(.*?)~/g, "<del>$1</del>").replace(/\n/g, "<br/>");
      return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  const getLivePreviewText = () => {
      if (selectedTemplateIndex !== "" && metaTemplates[selectedTemplateIndex]) {
          const selectedTemplate = metaTemplates[selectedTemplateIndex];
          const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
          
         if (bodyComponent) {
              let text = bodyComponent.text;
              text = text.replace(/\{\{1\}\}/g, "Rahul"); 
              
              templateVariables.forEach((val, idx) => {
                  if (idx > 0) { 
                      const regex = new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g');
                      text = text.replace(regex, val || `[Box ${idx + 1} ka text]`);
                  }
              });
              return formatWhatsAppText(text);
          }
      }
      return customMessage ? formatWhatsAppText(customMessage) : <span className="text-slate-400 italic text-[12px]">Type your message to see preview...</span>;
  };

  const [sourceFilter, setSourceFilter] = useState('ALL');
  
  const filteredLeads = leads.filter(lead => {
      if (sourceFilter === 'ALL') return true;
      if (sourceFilter === 'ORGANIC') return lead.source !== 'WhatsApp Blast' && lead.source !== 'CSV Upload';
      return lead.source === sourceFilter;
  });
  const enrolledCount = leads.filter(l => l.status === "ENROLLED").length;
  const totalSpent = campaignHistory.reduce((acc, curr) => acc + curr.cost, 0);
  const totalSent = campaignHistory.reduce((acc, curr) => acc + curr.sent, 0);
  
  const walletBalance = clientData?.walletBalance || 0;
  const isLowBalance = walletBalance < LOW_BALANCE_THRESHOLD;

  if (!currentClientId) {
      return <div className="p-10 text-center text-slate-500 font-bold">Loading Workspace...</div>;
  }

  return (
    <div className="bg-[#F8FAFC] min-h-screen p-4 md:p-8 space-y-6 pb-24">
      {/* LOW BALANCE ALERT */}
      {isLowBalance && (
          <div className="bg-rose-50 border border-rose-200 p-4 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
              <div className="flex items-center gap-3">
                  <div className="bg-rose-100 p-2 rounded-full"><AlertTriangle size={20} className="text-rose-600" /></div>
                  <div>
                      <h4 className="text-sm font-black uppercase tracking-wider text-rose-800">Low Credits Alert</h4>
                      <p className="text-xs font-medium mt-0.5 text-rose-600">Aapka balance ₹{LOW_BALANCE_THRESHOLD} se kam hai. Campaign failures se bachne ke liye abhi top-up karein.</p>
                  </div>
              </div>
              <button onClick={triggerRechargeModal} className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-black px-6 py-2.5 rounded-xl shadow-md transition-colors uppercase tracking-wider w-full sm:w-auto">
                  Recharge Now
              </button>
          </div>
      )}

      {/* TABS & COMPACT WALLET ROW */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full">
          <div className="flex p-1.5 bg-white border border-slate-200 rounded-xl w-full sm:max-w-sm shadow-sm">
            <button onClick={() => setActiveTab('DATABASE')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'DATABASE' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                <Database size={16}/> Leads
            </button>
            <button onClick={() => setActiveTab('HISTORY')} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'HISTORY' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                <History size={16}/> Logs
            </button>
          </div>

          <div className={`px-4 py-2 rounded-xl flex items-center gap-3 shadow-sm border shrink-0 transition-colors w-full sm:w-auto ${isLowBalance ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`}>
              <div className={`p-1.5 rounded-lg ${isLowBalance ? 'bg-rose-100 text-rose-600' : 'bg-emerald-50 text-emerald-500'}`}>
                  <IndianRupee size={16} />
              </div>
              <div className="flex-1 sm:flex-none">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Blast Credits</p>
                  <h4 className="text-sm font-black text-slate-800">₹{walletBalance.toFixed(2)}</h4>
              </div>
              <button onClick={triggerRechargeModal} className="ml-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider border border-indigo-100 shrink-0">
                  <Zap size={12}/> Recharge
              </button>
          </div>
      </div>

      {activeTab === 'DATABASE' && (
        <div className="space-y-6 animate-in fade-in">
            {/* KPI CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-slate-200 p-6 rounded-2xl flex flex-col justify-center shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Contacts</p>
                    <h3 className="text-4xl font-black text-slate-800">{leads.length}</h3>
                </div>

                <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-6 rounded-2xl relative flex flex-col justify-between overflow-hidden shadow-lg group">
                    <div className="relative z-10 text-white">
                        <p className="text-[10px] font-black text-indigo-200 uppercase tracking-widest mb-1">Auto-SEO Queue (Enrolled)</p>
                        <h3 className="text-4xl font-black">{enrolledCount}</h3>
                    </div>
                    <button onClick={openCampaignPreview} disabled={enrolledCount === 0 && selectedRows.length === 0} className="mt-4 w-full bg-white text-indigo-700 font-black py-3 rounded-xl text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all shadow-md disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center gap-2 relative z-10">
                        <Zap size={16} /> Run Drip Campaign
                    </button>
                    <Zap className="absolute -right-4 -bottom-4 w-32 h-32 text-indigo-400 opacity-20" />
                </div>

                <div className="bg-emerald-50 border border-emerald-200 p-6 rounded-2xl flex flex-col justify-center gap-4 border-dashed shadow-sm">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-black text-emerald-800 uppercase tracking-widest">Import Leads</h3>
                        <button onClick={downloadSampleCSV} className="text-[10px] font-bold text-emerald-600 bg-white hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 border border-emerald-200 shadow-sm"><FileText size={12}/> Template</button>
                    </div>
                    <label className={`cursor-pointer bg-emerald-600 text-white font-black px-4 py-3.5 rounded-xl text-xs uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-md flex items-center justify-center gap-2 w-full ${isUploading ? 'opacity-50' : ''}`}>
                        {isUploading ? <><Loader2 size={16} className="animate-spin"/> Processing CSV...</> : <><UploadCloud size={16}/> Select CSV File</>}
                        <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} disabled={isUploading} />
                    </label>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex justify-between items-center bg-slate-50 border-b border-slate-200 px-6 py-3 rounded-t-2xl">
                <h3 className="font-black text-xs text-slate-500 uppercase tracking-widest">Database Explorer</h3>
                <select 
                    value={sourceFilter} 
                    onChange={(e) => setSourceFilter(e.target.value)} 
                    className="bg-white border border-slate-200 text-slate-700 text-[10px] font-black uppercase tracking-widest rounded-lg px-3 py-1.5 outline-none cursor-pointer shadow-sm hover:border-indigo-300 transition-colors"
                >
                    <option value="ALL">🌍 All Leads</option>
                    <option value="ORGANIC">📍 Organic (Google / Meta Ads)</option>
                    <option value="WhatsApp Blast">🚀 WhatsApp Blast</option>
                    <option value="CSV Upload">📁 CSV Uploads</option>
                </select>
            </div>
                <div className="overflow-y-auto max-h-[500px] custom-scrollbar">
                    <table className="w-full text-left table-fixed">
                        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-500 uppercase tracking-widest z-20">
                            <tr>
                                <th className="px-6 py-4 w-16"><input type="checkbox" onChange={handleSelectAll} checked={selectedRows.length === leads.length && leads.length > 0} className="w-4 h-4 rounded border-slate-300 text-indigo-600" /></th>
                                <th className="px-6 py-4">Student Name</th>
                                <th className="px-6 py-4">Phone Number</th>
                                <th className="px-6 py-4">Current Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                           {filteredLeads.map((lead) => (
                                <tr key={lead.id} className={`hover:bg-slate-50 transition-colors ${selectedRows.includes(lead.id) ? 'bg-indigo-50/50' : ''}`}>
                                    <td className="px-6 py-4"><input type="checkbox" checked={selectedRows.includes(lead.id)} onChange={() => handleSelectRow(lead.id)} className="w-4 h-4 rounded border-slate-300 text-indigo-600" /></td>
                                    
                                    <td className="px-6 py-4">
                                        <div className="group/editname flex items-center gap-2">
                                            <span className="text-sm font-bold text-slate-800 truncate max-w-[150px]">
                                                {lead.studentName || 'Student'}
                                            </span>
                                            <button 
                                                onClick={(e) => handleEditName(lead.id, lead.studentName || 'Student', e)}
                                                className="opacity-0 group-hover/editname:opacity-100 text-slate-400 hover:text-indigo-600 transition-opacity"
                                                title="Edit Name"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                        </div>
                                        {lead.source === "WhatsApp Blast" && (
                                            <span className="inline-block mt-1 bg-rose-100 text-rose-700 text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider">
                                                🚀 Blast Lead
                                            </span>
                                        )}
                                    </td>

                                    <td className="px-6 py-4 text-sm font-mono text-slate-600">+{lead.phone}</td>
                                    <td className="px-6 py-4"><span className="text-[10px] font-black px-3 py-1.5 rounded-lg border bg-slate-50 border-slate-200 uppercase">{lead.status}</span></td>
                                </tr>
                            ))}
                            {leads.length === 0 && <tr><td colSpan="4" className="text-center py-10 text-slate-400 font-bold">No leads found. Please upload a CSV.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
      )}

      {activeTab === 'HISTORY' && (
        <div className="space-y-6 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 p-6 rounded-2xl flex items-center gap-4 shadow-sm">
                    <div className="bg-indigo-50 p-4 rounded-2xl"><Send size={24} className="text-indigo-600" /></div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Broadcasted</p>
                        <h3 className="text-3xl font-black text-slate-800">{totalSent} <span className="text-sm font-bold text-slate-400">Msgs</span></h3>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 p-6 rounded-2xl flex items-center gap-4 shadow-sm">
                    <div className="bg-rose-50 p-4 rounded-2xl"><IndianRupee size={24} className="text-rose-600" /></div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Cost Incurred</p>
                        <h3 className="text-3xl font-black text-slate-800">₹{totalSpent.toFixed(2)}</h3>
                    </div>
                </div>
            </div>
            
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-6 py-5"><h3 className="font-black text-sm text-slate-800 uppercase tracking-widest">Campaign Logs</h3></div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-white border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <tr><th className="px-6 py-4">Date</th><th className="px-6 py-4">Campaign Name</th><th className="px-6 py-4 text-center">Delivered</th><th className="px-6 py-4 text-center">Failed</th><th className="px-6 py-4 text-right">Cost</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {campaignHistory.map((log) => (
                                <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4"><p className="text-sm font-bold text-slate-800">{log.date.toLocaleDateString()}</p><p className="text-[10px] text-slate-400 font-mono mt-0.5">{log.date.toLocaleTimeString()}</p></td>
                                    <td className="px-6 py-4"><span className="text-[10px] font-black px-2 py-0.5 rounded border uppercase tracking-wider bg-slate-50 border-slate-200">{log.type}</span><p className="text-sm font-bold text-slate-700 mt-1">{log.name}</p></td>
                                    <td className="px-6 py-4 text-center"><span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-sm"><CheckCircle size={16}/> {log.sent}</span></td>
                                    <td className="px-6 py-4 text-center"><span className="inline-flex items-center gap-1 text-slate-400 font-bold text-sm"><X size={16}/> {log.failed}</span></td>
                                    <td className="px-6 py-4 text-right"><p className="text-sm font-black text-slate-800">₹{log.cost.toFixed(2)}</p></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
      )}

      {selectedRows.length > 0 && activeTab === 'DATABASE' && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-700 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-5 z-[500] animate-in slide-in-from-bottom-10">
              <span className="font-bold text-sm bg-slate-800 px-4 py-2 rounded-xl flex items-center gap-2 border border-slate-700"><CheckSquare size={16} className="text-indigo-400" /> {selectedRows.length} Selected</span>
              <div className="h-8 w-px bg-slate-700"></div>
              <button onClick={() => setIsComposeOpen(true)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-xl font-black text-sm flex items-center gap-2 transition-colors shadow-lg shadow-indigo-500/20 uppercase tracking-widest"><MessageSquare size={16}/> Compose Blast</button>
              <div className="h-8 w-px bg-slate-700"></div>
              <select onChange={(e) => { handleBulkStatusChange(e.target.value); e.target.value = ""; }} className="bg-slate-800 text-white border border-slate-700 rounded-xl px-4 py-2.5 text-sm font-bold outline-none hover:bg-slate-700 cursor-pointer">
                  <option value="">Change Status...</option><option value="ENROLLED">Mark ENROLLED</option><option value="DROPPED">Mark DROPPED</option>
              </select>
              <button onClick={handleBulkDelete} className="hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 p-2.5 rounded-xl font-bold flex items-center transition-colors border border-transparent hover:border-rose-500/30" title="Delete Selected"><Trash2 size={20}/></button>
          </div>
      )}

      {isComposeOpen && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-6xl shadow-2xl overflow-hidden flex flex-col h-[95vh] animate-in zoom-in duration-200 border border-slate-200/50">
                
                <div className="p-6 bg-white border-b border-slate-100 flex justify-between items-center shrink-0 z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 border border-indigo-100"><Rocket size={24} className="animate-pulse" /></div>
                        <div>
                            <h3 className="font-black text-2xl tracking-tight text-slate-900">Campaign Builder</h3>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1 flex items-center gap-2"><Users size={12}/> Targeting {selectedRows.length} Contacts</p>
                        </div>
                    </div>
                    {!isCampaignRunning && <button onClick={() => setIsComposeOpen(false)} className="w-10 h-10 bg-slate-50 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-full flex items-center justify-center transition-all"><X size={20}/></button>}
                </div>

                <div className="flex flex-col lg:flex-row flex-1 overflow-hidden bg-slate-50/50 relative">
                    
                    <div className="flex-1 p-6 md:p-8 border-b md:border-b-0 md:border-r border-slate-200 flex flex-col overflow-y-auto custom-scrollbar bg-white z-10 h-full gap-6">
                        
                        <div className="space-y-3">
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-600">1</span> Audience Selection</h3>
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                                <div><p className="text-sm font-bold text-slate-800">Custom Selection</p><p className="text-xs font-medium text-slate-500 mt-0.5">Selected from Database</p></div>
                                <div className="bg-indigo-100 text-indigo-600 px-3 py-1 rounded-lg font-black text-sm border border-indigo-200 shadow-sm">{selectedRows.length} Leads</div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-600">2</span> Meta Templates</h3>
                                <div className="flex gap-2">
                                    <button onClick={handleAutoSetupTemplates} disabled={isAutoSettingUp} className="text-[11px] font-black text-amber-600 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors shadow-sm disabled:opacity-50" title="Auto-Install Master Template to Meta">{isAutoSettingUp ? <Loader2 size={14} className="animate-spin"/> : <Wand2 size={14}/>} Auto-Setup ⚡</button>
                                    <button onClick={handleFetchTemplates} disabled={isFetchingTemplates} className="text-[11px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50">{isFetchingTemplates ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Sync Meta</button>
                                </div>
                            </div>
                            <div className="bg-white border border-slate-200 rounded-2xl p-1 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-50/50 transition-all">
                                {metaTemplates.length > 0 ? (
                                    <div className="flex flex-col gap-2 p-1">
                                        <select value={selectedTemplateIndex} onChange={handleTemplateSelect} className="w-full p-2.5 rounded-lg border-none bg-transparent text-sm font-bold text-slate-800 outline-none cursor-pointer focus:ring-0">
                                            <option value="">Choose an approved template...</option>
                                            {metaTemplates.map((t, idx) => <option key={t.id} value={idx}>{t.name} ({t.language})</option>)}
                                        </select>
                                    </div>
                                ) : (
                                    <div className="p-4 text-center bg-slate-50 rounded-xl">
                                        <p className="text-xs font-bold text-slate-400 mb-3">Click 'Sync Meta' to load templates</p>
                                        <div className="flex flex-wrap gap-2 justify-center pt-3 border-t border-slate-200">
                                            <span className="w-full text-[9px] text-slate-400 uppercase tracking-widest font-black">Fallback Static Templates</span>
                                            {templates.map((t, idx) => <button key={idx} onClick={() => setCustomMessage(t.text)} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[10px] font-bold text-slate-600 hover:bg-slate-100 transition-all shadow-sm">{t.title}</button>)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-3 flex-1 flex flex-col min-h-[300px]">
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-slate-600">3</span> 
                                Message Content & Variables
                            </h3>
                            <div className="flex-1 flex flex-col bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-50 transition-all">
                                
                                <div className="bg-slate-50/80 border-b border-slate-200 p-2.5 px-4 flex items-center justify-between shrink-0">
                                    <div className="flex items-center gap-1.5">
                                        <button onClick={() => insertText('*Bold*')} className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"><Bold size={16}/></button>
                                        <button onClick={() => insertText('_Italic_')} className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"><Italic size={16}/></button>
                                        <button onClick={() => insertText('~Strike~')} className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-lg transition-colors"><Strikethrough size={16}/></button>
                                        <div className="w-px h-5 bg-slate-300 mx-1"></div>
                                        <label className="w-8 h-8 flex items-center justify-center text-indigo-500 hover:text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer" title="Attach Image">
                                            <ImagePlus size={18}/>
                                            <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                                        </label>
                                    </div>
                                    {selectedTemplateIndex === "" && (
                                        <button onClick={() => insertText('[Name]')} className="bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-colors">+ Add [Name]</button>
                                    )}
                                </div>

                                {selectedTemplateIndex !== "" && templateVariables.length > 0 ? (
                                    <div className="flex-1 w-full p-5 space-y-6 overflow-y-auto custom-scrollbar bg-slate-50/30">
                                        {templateVariables.map((val, index) => (
                                            <div key={index} className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm relative group hover:border-indigo-300 transition-colors">
                                                <div className="absolute -top-2.5 left-4 bg-white px-2 text-[10px] font-black text-indigo-600 uppercase tracking-widest border border-slate-100 rounded-md shadow-sm">
                                                    Variable {'{{' + (index + 1) + '}}'}
                                                </div>
                                                
                                                {index === 0 ? (
                                                    <div className="flex items-center gap-2 text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-100 mt-2">
                                                        <span className="text-sm font-bold">[Lead Name Auto-Filled]</span>
                                                    </div>
                                                ) : (
                                                    <textarea 
                                                        rows="2"
                                                        value={val}
                                                        onChange={(e) => handleVariableChange(index, e.target.value)}
                                                        placeholder={`Enter content for {{${index + 1}}}...`}
                                                        className="w-full mt-2 p-0 border-none text-sm font-medium text-slate-800 placeholder:text-slate-400 focus:ring-0 outline-none resize-none custom-scrollbar"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <textarea 
                                        value={customMessage} 
                                        onChange={(e) => setCustomMessage(e.target.value)} 
                                        placeholder="Type your WhatsApp broadcast message here..."
                                        className="flex-1 w-full p-5 text-sm font-medium text-slate-700 outline-none resize-none custom-scrollbar min-h-[150px]"
                                    />
                                )}

                                {imagePreviewUrl && (
                                    <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center gap-3 shrink-0 animate-in slide-in-from-bottom-2">
                                        <img src={imagePreviewUrl} alt="preview" className="w-10 h-10 object-cover rounded shadow-sm border border-slate-200" />
                                        <p className="text-[11px] font-bold text-slate-500 truncate flex-1">{campaignImage?.name}</p>
                                        <button onClick={removeImage} className="text-rose-400 hover:text-rose-600 hover:bg-rose-100 p-2 rounded-lg transition-colors"><X size={16}/></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="hidden lg:flex w-[45%] bg-slate-100/50 border-l border-slate-200 p-6 items-center justify-center relative overflow-hidden h-full">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-gradient-to-br from-indigo-100 to-emerald-50 opacity-50 blur-3xl rounded-full pointer-events-none"></div>

                        <div className="w-[300px] max-h-[550px] h-full bg-slate-900 rounded-[40px] shadow-[0_20px_50px_rgba(0,0,0,0.2)] p-2 relative z-10 shrink-0 ring-4 ring-slate-800 flex flex-col mx-auto aspect-[9/19]">
                            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[80px] h-[24px] bg-black rounded-full z-30 shadow-inner"></div>
                            
                            <div className="w-full h-full bg-[#efeae2] rounded-[32px] overflow-hidden flex flex-col relative border border-slate-800">
                                <div className="bg-[#008069] pt-10 pb-3 px-4 flex items-center gap-3 text-white shadow-md relative z-20 shrink-0">
                                    <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center font-black text-sm backdrop-blur-sm">R</div>
                                    <div><h4 className="text-[14px] font-bold leading-none">Rahul Sharma</h4><p className="text-[10px] text-white/80 font-medium mt-0.5">Live Preview</p></div>
                                </div>

                                <div className="flex-1 p-3 overflow-y-auto custom-scrollbar relative flex flex-col items-start h-full">
                                    <div className="absolute inset-0 opacity-[0.06] pointer-events-none z-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
                                    <div className="w-full flex justify-center mb-4 mt-2 relative z-10"><span className="bg-[#E1F3FB] text-slate-600 text-[9px] font-bold px-2.5 py-1 rounded-md shadow-sm">TODAY</span></div>
                                    
                                    <div className="bg-white p-2.5 rounded-xl rounded-tl-sm shadow-sm max-w-[90%] text-[13px] text-[#111B21] relative self-start group z-10 flex flex-col">
                                        {imagePreviewUrl && <div className="mb-2 w-full rounded-lg overflow-hidden border border-slate-100 bg-slate-100"><img src={imagePreviewUrl} alt="Chat Media" className="w-full h-auto max-h-[140px] object-cover" /></div>}
                                        <div className="whitespace-pre-wrap pr-10 leading-relaxed font-normal">{getLivePreviewText()}</div>
                                        <span className="text-[9px] text-slate-400 absolute bottom-1 right-2 font-medium bg-white/90 px-1 rounded flex items-center gap-1">12:00 PM <span className="text-blue-500">✓✓</span></span>
                                        <div className="absolute top-0 -left-1.5 w-0 h-0 border-t-[10px] border-t-white border-l-[10px] border-l-transparent"></div>
                                    </div>

                                    {isCampaignRunning && (
                                        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-30 flex flex-col items-center justify-center"><div className="bg-white px-5 py-4 rounded-2xl shadow-xl border border-indigo-100 flex flex-col items-center gap-3"><Loader2 size={28} className="text-indigo-600 animate-spin"/><span className="text-xs font-black text-slate-800 uppercase tracking-widest">Dispatching</span></div></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-6 md:px-8 py-4 bg-white border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0 z-10">
                    <div className={`px-5 py-3 rounded-xl border flex items-center gap-4 ${canAffordCampaign(selectedRows.length) ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
                        <IndianRupee size={24}/>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-widest mb-0.5">Total Cost Estimate</p>
                            <p className="text-base font-bold">₹{(selectedRows.length * MESSAGE_COST).toFixed(2)}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                        <button onClick={() => setIsComposeOpen(false)} disabled={isCampaignRunning} className="px-6 py-3 rounded-xl text-xs font-black text-slate-500 hover:text-slate-800 hover:bg-slate-50 transition-colors uppercase tracking-widest disabled:opacity-50">Cancel</button>
                        <button onClick={handleSendCustomMessage} disabled={isCampaignRunning || (!customMessage && !campaignImage) || !currentClientId} className={`text-white font-black px-8 py-3 rounded-xl text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-xl disabled:opacity-50 disabled:shadow-none ${canAffordCampaign(selectedRows.length) ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200' : 'bg-slate-300'}`}>
                            {canAffordCampaign(selectedRows.length) ? (isCampaignRunning ? <><Loader2 size={16} className="animate-spin"/> Sending...</> : <>Launch Broadcast <Send size={16}/></>) : "No Balance"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
      )}

      {/* SEO DRIP PREVIEW MODAL */}
      {isPreviewOpen && createPortal(
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
              <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in duration-200">
                  <div className="p-6 bg-slate-50 border-b flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="bg-indigo-600 p-1.5 rounded-lg text-white"><Send size={16}/></div>
                        <h3 className="font-black text-sm uppercase tracking-widest text-slate-800">SEO Drip Ready</h3>
                      </div>
                      {!isCampaignRunning && <button onClick={() => setIsPreviewOpen(false)} className="text-slate-400 hover:text-rose-500"><X size={20}/></button>}
                  </div>
                  <div className="p-6 space-y-4">
                      <div className={`p-4 rounded-xl border flex items-start gap-3 mb-2 ${canAffordCampaign(selectedForCampaign.length) ? 'bg-indigo-50 border-indigo-100' : 'bg-rose-50 border-rose-200'}`}>
                          <div className={`p-1.5 rounded-lg ${canAffordCampaign(selectedForCampaign.length) ? 'bg-indigo-100 text-indigo-600' : 'bg-rose-100 text-rose-600'}`}><IndianRupee size={16}/></div>
                          <div>
                              <p className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${canAffordCampaign(selectedForCampaign.length) ? 'text-indigo-600' : 'text-rose-600'}`}>Estimated Cost</p>
                              <p className="text-xs font-bold text-slate-700">{selectedForCampaign.length} Messages x ₹{MESSAGE_COST.toFixed(2)} = <span className={canAffordCampaign(selectedForCampaign.length) ? 'text-indigo-600' : 'text-rose-600 font-black'}>₹{(selectedForCampaign.length * MESSAGE_COST).toFixed(2)}</span></p>
                          </div>
                      </div>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">AI selected {selectedForCampaign.length} students:</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                          {selectedForCampaign.map((s, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-slate-50 p-3 rounded-xl border border-slate-100">
                                  <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[10px] font-black text-indigo-600 border border-indigo-100 shadow-sm">{s.studentName?.[0]}</div>
                                      <div>
                                          <p className="text-[13px] font-black text-slate-800 leading-none">{s.studentName}</p>
                                          <p className="text-[10px] text-slate-400 font-mono mt-1">+{s.phone}</p>
                                      </div>
                                  </div>
                                  {idx < progressCount ? <CheckCircle size={18} className="text-emerald-500 animate-in zoom-in" /> : isCampaignRunning && idx === progressCount ? <Loader2 size={18} className="text-indigo-500 animate-spin" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-200" />}
                              </div>
                          ))}
                      </div>
                  </div>
                  <div className="p-6 pt-0">
                      <button 
                        onClick={handleLaunchCampaign}
                        disabled={isCampaignRunning || !canAffordCampaign(selectedForCampaign.length)}
                        className={`w-full text-white font-black py-4 rounded-2xl text-sm uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-xl disabled:opacity-70 ${canAffordCampaign(selectedForCampaign.length) ? 'bg-slate-900 hover:bg-indigo-600 shadow-indigo-100' : 'bg-slate-300'}`}
                      >
                          {canAffordCampaign(selectedForCampaign.length) ? (isCampaignRunning ? <><Loader2 size={18} className="animate-spin"/> Sending Messages...</> : "Confirm & Launch 🚀") : "Insufficient Balance"}
                      </button>
                  </div>
              </div>
          </div>,
          document.body 
      )}
    </div>
  );
};

export default ClientAudience;